import React, { useEffect, useState } from 'react';

import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import LinearProgress from '@mui/material/LinearProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { LoginResponse } from '../../@types/LoginResponse';
import { useAppState } from '../../context/AppState';
import { useIntelState } from '../../context/IntelState';
import { setToken } from '../../services/Auth';
import RestUtilities from '../../services/RestUtilities';
import InlineError from '../Common/InlineError';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { appState, appDispatch } = useAppState();
  const { intelState, intelDispatch } = useIntelState();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [disabled, setDisabled] = useState(false);
  const [feedback, setFeedback] = useState<string>('');
  const [errors, setErrors] = useState(null);
  const isPasswordInvalid = !!(password.length < 3 || password.length > 30);
  const isUsernameInvalid = !!(username.length < 3 || username.length > 32);
  const hasErrors = isUsernameInvalid || isPasswordInvalid;

  const tokenParam = searchParams.get('t');
  const serverParam = searchParams.get('s');

  useEffect(() => {
    if (serverParam === '3') {
      intelDispatch({
        type: 'SET_SERVER',
        payload: 3,
      });
      appDispatch({ type: 'SET_IS_DEV', payload: true });
    } else if (serverParam === '1' || serverParam === '2') {
      const server = Number(serverParam);
      intelDispatch({
        type: 'SET_SERVER',
        payload: server,
      });
      if (intelState.enemyData?.server !== server) {
        intelDispatch({ type: 'SET_ENEMY', payload: '1:1' });
        intelDispatch({ type: 'SET_ENEMY_DATA', payload: undefined });
        intelDispatch({ type: 'SET_ENEMY_PLAN', payload: undefined });
      }
      if (intelState.ownData?.server !== server) {
        intelDispatch({ type: 'SET_OWN_DATA', payload: undefined });
        intelDispatch({ type: 'SET_OWN_PLAN', payload: undefined });
      }
    }
    if (tokenParam) {
      setToken(tokenParam);
      navigate('/');
    }
  }, [
    tokenParam,
    navigate,
    serverParam,
    intelDispatch,
    appDispatch,
    intelState.enemyData?.server,
    intelState.ownData?.server,
  ]);

  const handleLoginClick = async (e: any) => {
    e.preventDefault();
    setDisabled(true);
    const url = 'auth/v1/login';
    const data = {
      password,
      username,
      isDev: appState.isDev || serverParam === '3',
    };
    const response = await RestUtilities.post(url, data);
    if (response.ok) {
      const loginResponse: LoginResponse = response.content;
      setToken(loginResponse.token);
      appDispatch({
        type: 'SET_TOKEN_CREDENTIALS',
        payload: loginResponse.tokenCredentials,
      });
      setDisabled(false);
      navigate('/');
    } else {
      setFeedback('The username and password combination was not recognised.');
      setDisabled(false);
      setErrors(null);
    }
  };

  return (
    <div className='Home'>
      <Container component='main' maxWidth='xs'>
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
            <LockOutlinedIcon />
          </Avatar>
          <Box
            component='form'
            onSubmit={handleLoginClick}
            noValidate
            sx={{ mt: 1 }}
          >
            <TextField
              variant='outlined'
              value={username}
              margin='normal'
              required
              fullWidth
              id='username'
              label='Username'
              name='username'
              autoComplete='username'
              autoFocus
              onChange={(event) => {
                setUsername(event.target.value);
                setFeedback('');
              }}
              disabled={disabled}
              error={isUsernameInvalid && !!username}
              helperText={
                isUsernameInvalid &&
                !!username &&
                'Must be minimum 5 characters and maximum of 32'
              }
            />
            <TextField
              variant='outlined'
              value={password}
              margin='normal'
              required
              fullWidth
              name='password'
              label='Password'
              type='password'
              id='password'
              autoComplete='current-password'
              onChange={(event) => {
                setPassword(event.target.value);
                setFeedback('');
              }}
              error={!!password && isPasswordInvalid}
              helperText={
                !!password &&
                isPasswordInvalid &&
                'Must be minimum 5 characters and maximum of 30'
              }
              disabled={disabled}
            />
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={appState.isDev}
                    onClick={() => {
                      appDispatch({
                        type: 'SET_IS_DEV',
                        payload: !appState.isDev,
                      });
                      intelDispatch({
                        type: 'SET_SERVER',
                        payload: appState.isDev ? 1 : 3,
                      });
                    }}
                  />
                }
                label='Dev Sever'
              />
            </FormGroup>
            <Button
              type='submit'
              fullWidth
              variant='contained'
              sx={{ mt: 3, mb: 2 }}
              onClick={(e) => handleLoginClick(e)}
              disabled={disabled || hasErrors}
            >
              Sign In
            </Button>
            {disabled && (
              <Box m={2}>
                <LinearProgress color='secondary' />
              </Box>
            )}
            {feedback && (
              <>
                <Typography component='p'>{feedback}</Typography>
                <InlineError field='username' errors={errors} />
                <InlineError field='password' errors={errors} />
              </>
            )}
          </Box>
        </Box>
      </Container>
    </div>
  );
};

export default Login;
